import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./qimen-dunjia-result-style.css";
import useIsMobile from "../../../../hooks/useIsMobile";
import { COMPASS_3X3, DIR_INNER_CORNER } from "../qimen-dunjia-constants";

//八門解析 / 操作步驟 / FAQ / 兇格說明 一律由後端 dictionary 提供（不再前端硬編）
//  找出某個吉門的解析（dictionary.gates 是陣列，用 name 對 results[].matchedGate）
const findGate = (dictionary, gateName) =>
  (dictionary?.gates || []).find((g) => g.name === gateName) || null;

//把後端的步驟模板代入方位（純字串替換，等同 i18n 填 placeholder）
const fillSteps = (dictionary, startDir, destDir) =>
  (dictionary?.steps || []).map((s) => ({
    ...s,
    title: (s.title || "").replaceAll("{startDir}", startDir).replaceAll("{destDir}", destDir),
    desc: (s.desc || "").replaceAll("{startDir}", startDir).replaceAll("{destDir}", destDir),
  }));

//把 F-3 回的 qimenPlate（trigramIndex 1~9 一維陣列）轉成 9 格 cells 陣列
//  排序：南上北下、東左西右（傳統奇門盤），對應 PlateCellView 顯示
//  位置 → cells index：
//    [0] 東南  [1] 南    [2] 西南
//    [3] 東    [4] 中宮  [5] 西
//    [6] 東北  [7] 北    [8] 西北
//  trigramIndex → cells index 對照（依 position 推導）：
const TRIGRAM_TO_CELL = {
  4: 0,
  9: 1,
  2: 2,
  3: 3,
  5: 4,
  7: 5,
  8: 6,
  1: 7,
  6: 8,
};

//把 F-3 plateSnapshot 轉成 QimenPlate 元件吃的格式
//  plateSnapshot 包含：qimenPlate / realFushou(符首) / realDirectOfficer(直使) / realDirectFu(直符)
//                       + 自加的 plate(陽4局) / fourPillars(年月日時)
const buildPlateData = (snapshot, matchedGate, plateType) => {
  if (!snapshot?.qimenPlate?.length) return null;
  //日盤(plateType "2") 與時盤欄位完全不同：
  //  時盤 → 天盤干 / 地盤干 / 九星 / 八門 / 八神
  //  日盤 → 方位 / 日盤九星(含吉凶) / 八門 / 十二神(地支+神+吉凶)
  //  （日盤的 heavenPlate / earthPlate / eightGod 本來就是空的，畫了也沒東西）
  const isDay = plateType === "2";
  const cells = Array(9).fill(null);
  for (const raw of snapshot.qimenPlate) {
    const idx = TRIGRAM_TO_CELL[raw.trigramIndex];
    if (idx === undefined) continue;

    if (isDay) {
      const isCenter = raw.trigramIndex === 5;
      cells[idx] = {
        isDay: true,
        direction: isCenter ? "" : raw.position || "",
        gate: isCenter
          ? "中宮"
          : raw.eightDoor
            ? `${raw.eightDoor}門`
            : "",
        dayStar: raw.day?.nightStar || "",
        dayStarBad: raw.day?.nightStarLuck === false,
        twelveGod: raw.day?.twelveGod || [],
        //兇格（canUse=false，reason 如「天神煞」「九星凶」）→ 標驚嘆號；中宮不算兇格
        prohibited: isCenter ? false : !raw.canUse,
        reason: raw.reason || "",
      };
      continue;
    }

    if (raw.trigramIndex === 5) {
      //中宮顯示「直符 直使 符首」三柱（每柱直書：label 兩字 + 值 一字）
      cells[idx] = {
        isCenter: true,
        centerColumns: [
          { label: "直符", value: snapshot.realDirectFu || "" },
          { label: "直使", value: snapshot.realDirectOfficer || "" },
          { label: "符首", value: snapshot.realFushou || "" },
        ],
        direction: "中",
      };
    } else {
      cells[idx] = {
        gan: raw.heavenPlate || "",
        star: raw.nightStar || "",
        gate: raw.eightDoor ? `${raw.eightDoor}門` : "",
        earthGan: raw.earthPlate || "",
        direction: raw.position || "",
        spirit: raw.eightGod || "", //八神（符/蛇/陰/天/合/地/雀/陳）
        prohibited: !raw.canUse,
      };
    }
  }
  return { cells, matchedGate };
};

//奇門遁甲 — result 頁
//  user mode（無 data prop）：從 location.state 帶 { event, plate, date, results }
//  admin mode（傳 data prop）：直接吃 prop，不檢查 / 不跳轉（給後台「查看產品」用）
const QimenDunjiaResultPage = ({ data: dataProp = null } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const source = dataProp ?? location.state ?? {};
  const { event, plate, date, results, dictionary } = source;
  const isAdminMode = !!dataProp;

  //避開全站 nav 遮住內容
  const [navHeight, setNavHeight] = useState(0);
  useEffect(() => {
    const nav = document.querySelector(
      isMobile ? ".mobile-dashboard nav" : ".dashboard nav",
    );
    setNavHeight(nav ? nav.offsetHeight : 0);
  }, [isMobile]);

  //沒帶資料 → 回 enter（防直接 URL 進來）；admin mode 不檢查
  useEffect(() => {
    if (isAdminMode) return;
    if (!event || !plate || !date || !results) {
      navigate("/qimen-dunjia", { replace: true });
    }
  }, [isAdminMode, event, plate, date, results, navigate]);

  const [view, setView] = useState("list"); // "list" | "detail"
  const [selectedResult, setSelectedResult] = useState(null);

  const goDetail = (r) => {
    setSelectedResult(r);
    setView("detail");
  };

  const handleBack = () => {
    if (view === "detail") {
      setSelectedResult(null);
      setView("list");
    } else {
      navigate("/qimen-dunjia/pass"); //回 pass 重選
    }
  };

  const handleClose = () => navigate("/qimen-dunjia");
  const handleReset = () => navigate("/qimen-dunjia/pass");

  if (!event || !plate) return null;

  return (
    <div className="qimen-dunjia-page">
      <main className="mobile-main" style={{ paddingTop: navHeight }}>
        <div className="wizard">
          {/* 頂部 header */}
          <div className="wizard-header">
            <h2 className="wizard-title">
              {view === "list" ? "吉時結果" : "吉時詳情"}
            </h2>
          </div>

          {/* 吉時清單 */}
          {view === "list" && (
            <div className="step-content step-result">
              <div className="result-summary">
                <div className="result-tags">
                  <div className={`event-tag inline color-${event.color}`}>
                    <i className={`bi ${event.icon}`}></i>
                    <span>{event.label}</span>
                  </div>
                  <div className="plate-tag inline">
                    <i className={`bi ${plate.icon}`}></i>
                    <span>{plate.label}</span>
                  </div>
                </div>
                <p className="count-text">
                  共 {results.length} 個吉{plate.id === "1" ? "時" : "日"}
                </p>
              </div>

              {results.length === 0 ? (
                <div className="empty-state">
                  <i className="bi bi-emoji-frown"></i>
                  <p className="empty-title">查無可用吉時</p>
                  <p className="empty-sub">
                    這個日期 / 區間內沒有對應的吉
                    {plate.id === "1" ? "時" : "日"}
                    ，建議換一天或拉長日盤天數試試。
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="result-section-title">
                    <i
                      className={`bi ${plate.id === "1" ? "bi-clock-fill" : "bi-calendar-check-fill"}`}
                    ></i>{" "}
                    {plate.id === "1" ? "適合時辰" : "適合日期"}
                  </h3>

                  <div className="result-list">
                    {results.map((r, i) => (
                      <div
                        key={i}
                        className="result-card"
                        onClick={() => goDetail(r)}
                      >
                        <div className="result-time">
                          {plate.id === "1" ? (
                            <>
                              <p className="time-range">{r.timeRange}</p>
                              <p className="chinese-time">{r.chineseTime}</p>
                            </>
                          ) : (
                            <>
                              <p className="time-range">{r.date.slice(5)}</p>
                              <p className="chinese-time">
                                {r.date.slice(0, 4)}
                              </p>
                            </>
                          )}
                        </div>
                        <div className="result-info">
                          <div className="dir-gate">
                            <span className="arrow">{r.arrow}</span>
                            <span className="direction">{r.direction}</span>
                            <span className="gate-badge">{r.matchedGate}</span>
                          </div>
                          <p className="desc">
                            {findGate(dictionary, r.matchedGate)
                              ?.description?.slice(0, 30) ||
                              `${r.matchedGate} 落於 ${r.direction}，宜進行此事`}
                            …
                          </p>
                        </div>
                        <div className="goto-hint">
                          <span>查看詳情</span>
                          <i className="bi bi-chevron-right"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* <button className="secondary-btn" onClick={handleReset}>
                重新查詢
              </button> */}
            </div>
          )}

          {/* 詳情 */}
          {view === "detail" && selectedResult && (
            <ResultDetail
              result={selectedResult}
              plateType={plate.id}
              dictionary={dictionary}
              onBack={() => {
                setSelectedResult(null);
                setView("list");
              }}
            />
          )}
        </div>
      </main>
    </div>
  );
};

//—— 箭頭 overlay：從起點 cell 的內角畫到目的地 cell 的內角 ——
const CompassArrow = ({ startDir, destDir }) => {
  const start = DIR_INNER_CORNER[startDir];
  const dest = DIR_INNER_CORNER[destDir];
  if (!start || !dest) return null;

  return (
    <svg
      className="compass-arrow"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      <defs>
        <marker
          id="arrowhead"
          markerWidth="5"
          markerHeight="5"
          refX="4"
          refY="2.5"
          orient="auto"
        >
          <polygon points="0 0, 5 2.5, 0 5" fill="rgb(185, 136, 0)" />
        </marker>
      </defs>
      <line
        x1={start.x}
        y1={start.y}
        x2={dest.x}
        y2={dest.y}
        stroke="rgb(185, 136, 0)"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeDasharray="2 1.5"
        markerEnd="url(#arrowhead)"
        opacity="0.5"
      />
    </svg>
  );
};

//—— 九宮盤面 ——
const QimenPlate = ({ data, highlightGate }) => (
  <div className="qimen-plate">
    {data.cells.map((cell, i) => {
      //日盤格：方位(左上) / 日盤九星+吉凶(右上) / 八門(中) / 十二神(底部)
      if (cell?.isDay) {
        const isHighlightDay = highlightGate && cell.gate === highlightGate;
        return (
          <div
            key={i}
            className={`plate-cell day ${cell.prohibited ? "prohibited" : ""} ${isHighlightDay ? "highlight" : ""}`}
          >
            <span className="plate-day-dir">{cell.direction}</span>
            <span
              className={`plate-day-star ${cell.dayStarBad ? "bad" : ""}`}
            >
              {cell.dayStar}
              {cell.dayStar && cell.dayStarBad ? "(凶)" : ""}
            </span>
            <span className="plate-gate">{cell.gate}</span>
            {cell.twelveGod.length > 0 && (
              <div className="plate-twelve-god">
                {cell.twelveGod.map((g, k) => (
                  <span
                    key={k}
                    className={`god-item ${g.luck ? "" : "bad"}`}
                  >
                    <span className="god-land">{g.land}</span>
                    <span className="god-name">
                      {g.god}
                      {g.god && !g.luck ? "(凶)" : ""}
                    </span>
                  </span>
                ))}
              </div>
            )}
            {cell.prohibited && (
              <div className="prohibit-overlay" title={cell.reason}>
                <i className="bi bi-exclamation-circle-fill"></i>
              </div>
            )}
          </div>
        );
      }
      if (cell.isCenter) {
        return (
          <div key={i} className="plate-cell center">
            {/* 3×3 grid：每 column = 一柱「直符心 / 直使開 / 符首戊」直書 */}
            {cell.centerColumns && (
              <div className="center-grid">
                {/* row 1：每柱 label 第 1 字 */}
                {cell.centerColumns.map((col, j) => (
                  <span key={`l1-${j}`} className="center-cell-char label">
                    {col.label[0]}
                  </span>
                ))}
                {/* row 2：每柱 label 第 2 字 */}
                {cell.centerColumns.map((col, j) => (
                  <span key={`l2-${j}`} className="center-cell-char label">
                    {col.label[1]}
                  </span>
                ))}
                {/* row 3：每柱對應值 */}
                {cell.centerColumns.map((col, j) => (
                  <span key={`v-${j}`} className="center-cell-char value">
                    {col.value}
                  </span>
                ))}
              </div>
            )}
            <span className="plate-direction">{cell.direction}</span>
          </div>
        );
      }
      const isHighlight = highlightGate && cell.gate === highlightGate;
      return (
        <div
          key={i}
          className={`plate-cell ${cell.prohibited ? "prohibited" : ""} ${isHighlight ? "highlight" : ""}`}
        >
          <span className="plate-gan">{cell.gan}</span>
          <span className="plate-star">{cell.star}</span>
          <span className="plate-gate">{cell.gate}</span>
          <span className="plate-earth-gan">{cell.earthGan}</span>
          <span className="plate-direction">{cell.direction}</span>
          <span className="plate-spirit">{cell.spirit}</span>
          {cell.prohibited && (
            <div className="prohibit-overlay">
              <i className="bi bi-exclamation-circle-fill"></i>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

//—— 詳情頁：解釋怎麼操作 ——
//  result 來自 F-3 的吉時 result：
//    時盤 → 有 timeRange + chineseTime
//    日盤 → 有 date（無 timeRange / chineseTime）
//    通用 → matchedGate / direction / arrow / plateSnapshot
const ResultDetail = ({ result, plateType, dictionary, onBack }) => {
  //起點方位由後端算好帶下來（吉方的對沖），前端不再自己做方位對沖邏輯
  const startDir = result.startDirection || "";
  const startBare = startDir.replace("方", "");
  const destBare = result.direction.replace("方", "");
  //步驟 / 八門解析 / FAQ / 兇格說明 全部來自後端 dictionary
  const steps = fillSteps(dictionary, startDir, result.direction);
  const gateInfo = findGate(dictionary, result.matchedGate);
  const [openFaq, setOpenFaq] = useState(null);

  //把 F-3 給的 plateSnapshot 轉成 9 格 cells（南上北下排序）
  const plateData = buildPlateData(
    result.plateSnapshot,
    result.matchedGate,
    plateType,
  );
  const snapshot = result.plateSnapshot || {};

  const getCellRole = (dir) => {
    if (dir === "中") return "center";
    if (dir === startBare) return "start";
    if (dir === destBare) return "dest";
    return "";
  };

  return (
    <div className="step-content step-detail">
      {/* 返回吉時清單 */}
      <button className="detail-back-btn" onClick={onBack}>
        <i className="bi bi-chevron-left"></i> 返回吉時清單
      </button>

      <div className="detail-summary">
        <div className="time-block">
          {plateType === "1" ? (
            <>
              <p className="time-range">{result.timeRange}</p>
              <p className="chinese-time">
                {result.date} {result.chineseTime}
              </p>
            </>
          ) : (
            <>
              <p className="time-range">{result.date.slice(5)}</p>
              <p className="chinese-time">{result.date.slice(0, 4)} 年</p>
            </>
          )}
        </div>
        <div className="dir-block">
          <span className="gate-badge">{result.matchedGate}</span>
          <span className="direction">
            <span className="arrow">{result.arrow}</span>
            目的地：{result.direction}
          </span>
        </div>
      </div>

      <h3 className="detail-section-title">
        <i className="bi bi-compass-fill"></i> 怎麼走
      </h3>
      <p className="detail-section-sub">
        起點在「<strong>{startDir}</strong>」，目的地在「
        <strong>{result.direction}</strong>」
      </p>

      <div className="compass-grid">
        {COMPASS_3X3.map((cell, i) => {
          const role = getCellRole(cell.dir);
          return (
            <div key={i} className={`dir-cell ${role}`}>
              {role === "center" ? (
                <>
                  <i className="bi bi-person-fill"></i>
                  <span className="cell-label">你</span>
                </>
              ) : (
                <>
                  <span className="dir-label">{cell.label}</span>
                  {role === "start" && (
                    <>
                      <i className="bi bi-geo-alt-fill role-icon"></i>
                      <span className="role-text">起點</span>
                    </>
                  )}
                  {role === "dest" && (
                    <>
                      <i className="bi bi-flag-fill role-icon"></i>
                      <span className="role-text">目的地</span>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })}
        <CompassArrow startDir={startBare} destDir={destBare} />
      </div>

      <h3 className="detail-section-title">
        <i className="bi bi-list-check"></i> 操作步驟
      </h3>
      <div className="step-cards">
        {steps.map((s, i) => (
          <div key={i} className="step-card">
            <div className="step-num">{i + 1}</div>
            <div className="step-text">
              <p className="step-title">{s.title}</p>
              <p className="step-desc">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {gateInfo && (
        <>
          <h3 className="detail-section-title">
            <i className="bi bi-book-fill"></i> {result.matchedGate} 解析
            {gateInfo.tier && (
              <span
                className={`gate-tier tier-${
                  gateInfo.tier.includes("大凶")
                    ? "great-bad"
                    : gateInfo.tier.includes("凶")
                      ? "bad"
                      : gateInfo.tier.includes("上上吉")
                        ? "great-good"
                        : gateInfo.tier.includes("上吉")
                          ? "good"
                          : "neutral"
                }`}
              >
                {gateInfo.tier}
              </span>
            )}
          </h3>
          <div className="gate-card">
            <div className="gate-header">
              <i className={`bi ${gateInfo.icon}`}></i>
              <span className="gate-summary">{gateInfo.summary}</span>
            </div>
            <p className="gate-desc">{gateInfo.description}</p>
            <div className="gate-suitable">
              <span className="label">適合：</span>
              {gateInfo.suitable.map((s, i) => (
                <span key={i} className="tag">
                  {s}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      <h3 className="detail-section-title">
        <i className="bi bi-grid-3x3-gap-fill"></i> 此時刻盤面
      </h3>

      {/* 盤面資訊：局數 + 四柱（時盤 / 日盤都顯示）*/}
      {snapshot.plate && (
        <div className="plate-info-bar">
          <div className="plate-label">{snapshot.plate}</div>
          {snapshot.fourPillars && (
            <table className="pillars-table">
              <thead>
                <tr>
                  <th>年</th>
                  <th>月</th>
                  <th>日</th>
                  <th>時</th>
                </tr>
              </thead>
              <tbody>
                {/* 干在上 */}
                <tr className="gan-row">
                  <td>{snapshot.fourPillars.year?.[0] || "—"}</td>
                  <td>{snapshot.fourPillars.month?.[0] || "—"}</td>
                  <td>{snapshot.fourPillars.day?.[0] || "—"}</td>
                  <td>{snapshot.fourPillars.time?.[0] || "—"}</td>
                </tr>
                {/* 支在下 */}
                <tr className="zhi-row">
                  <td>{snapshot.fourPillars.year?.[1] || "—"}</td>
                  <td>{snapshot.fourPillars.month?.[1] || "—"}</td>
                  <td>{snapshot.fourPillars.day?.[1] || "—"}</td>
                  <td>{snapshot.fourPillars.time?.[1] || "—"}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}

      {plateData ? (
        <QimenPlate data={plateData} highlightGate={result.matchedGate} />
      ) : (
        <p className="plate-empty">盤面資料載入中…</p>
      )}
      {dictionary?.badPatterns && (
        <div className="plate-note">
          <i className="bi bi-exclamation-circle-fill"></i>
          <div>
            <p className="note-title">{dictionary.badPatterns.title}</p>
            <p className="note-desc">{dictionary.badPatterns.description}</p>
          </div>
        </div>
      )}

      <h3 className="detail-section-title">
        <i className="bi bi-question-circle-fill"></i> 常見問題
      </h3>
      <div className="faq-list">
        {(dictionary?.faq || []).map((f, i) => (
          <div
            key={i}
            className={`faq-item ${openFaq === i ? "open" : ""}`}
            onClick={() => setOpenFaq(openFaq === i ? null : i)}
          >
            <div className="faq-q">
              <span>{f.question}</span>
              <i className="bi bi-chevron-down"></i>
            </div>
            {openFaq === i && <div className="faq-a">{f.answer}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default QimenDunjiaResultPage;
